/**
 * Vendor quote upload → classify → extract → persist.
 * Routes equipment schedules to the BoM review dialog when detected.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { SupplyChainPlan } from '../../../types/supplyChain';
import type { SiteDocument } from '../../../types/siteRag';
import type { ExtractedBomLine } from '../../../services/supplyChain/extractBomFromPdf';
import { extractTextFromFile } from '../../../services/knowledge/documentUpload';
import { maybeProcessUploadedFileAsRfqResponse } from '../../../services/knowledge/documentUpload';
import { useStore } from '../../../store';

export interface QuoteUploadBomCandidate {
  lines: ExtractedBomLine[];
  warnings: string[];
  documentId?: string;
}

export interface UseQuoteUploadFlowOptions {
  siteId: string;
  plan: SupplyChainPlan | undefined;
  onBomCandidate?: (candidate: QuoteUploadBomCandidate) => void;
}

function emptyPlanStub(): SupplyChainPlan {
  const now = new Date().toISOString();
  return {
    bom: [],
    suppliers: [],
    quotes: [],
    version: 1,
    generatedAt: now,
    generatedBy: 'manual',
    updatedAt: now,
    updatedBy: 'system',
  };
}

export function useQuoteUploadFlow({
  siteId,
  plan,
  onBomCandidate,
}: UseQuoteUploadFlowOptions) {
  const addSiteDocument = useStore(s => s.addSiteDocument);
  const applyRfqPipelineResult = useStore(s => s.applyRfqPipelineResult);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const openFilePicker = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const processFile = useCallback(async (file: File): Promise<boolean> => {
    const doc: SiteDocument = await addSiteDocument(siteId, file);
    toast.success(`Uploaded ${file.name}.`);

    const text = await extractTextFromFile(file);
    const docWithText: SiteDocument = { ...doc, textContent: text };
    const stubPlan = plan?.bom.length || plan?.quotes.length ? plan! : emptyPlanStub();

    const result = await maybeProcessUploadedFileAsRfqResponse({
      document: docWithText,
      supplyChainPlan: stubPlan,
    });

    if (result.status === 'bom-candidate') {
      onBomCandidate?.({
        lines: result.extraction.lines,
        warnings: result.extraction.warnings,
        documentId: doc.id,
      });
      toast.message('Equipment schedule detected — review extracted BoM lines.');
      return false;
    }

    if ('status' in result && (result.status === 'auto-routed' || result.status === 'orphan-quote')) {
      const res = await applyRfqPipelineResult(siteId, result);
      if (res.ok) {
        toast.success(
          result.status === 'auto-routed'
            ? 'Quote extracted and linked to BoM items.'
            : 'Quote extracted — review matches on the Quotes tab.',
        );
        return true;
      }
      toast.error(res.error);
      return false;
    }

    if (result.status === 'skipped') {
      if (result.reason === 'empty-plan') {
        toast.error(
          'Could not parse as a vendor quote. Add BoM lines first, or upload an equipment schedule as BoM.',
        );
      } else if (result.reason === 'no-plan') {
        toast.error('Supply chain plan unavailable — try again.');
      } else {
        toast.error(result.error ?? 'Quote extraction skipped — add BoM lines or try a different file.');
      }
      return false;
    }

    if ('status' in result && result.status === 'classified-only') {
      toast.message('Document looks like a quote but key fields were missing — add manually on the Quotes tab.');
      return false;
    }

    toast.error('Could not extract quote data from this file.');
    return false;
  }, [addSiteDocument, applyRfqPipelineResult, onBomCandidate, plan, siteId]);

  const handleFile = useCallback(async (file: File) => {
    setBusy(true);
    try {
      await processFile(file);
    } catch (e) {
      toast.error(`Quote upload failed: ${(e as Error)?.message ?? 'unknown'}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [processFile]);

  /** Batch mode — queue multiple PDFs sequentially (Compare view drag-drop). */
  const handleFiles = useCallback(async (files: FileList | readonly File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    if (list.length === 1) {
      await handleFile(list[0]!);
      return;
    }
    setBusy(true);
    let okCount = 0;
    try {
      for (let i = 0; i < list.length; i++) {
        toast.message(`Processing quote ${i + 1} of ${list.length}…`);
        try {
          const ok = await processFile(list[i]!);
          if (ok) okCount += 1;
        } catch (e) {
          toast.error(`Quote ${i + 1} failed: ${(e as Error)?.message ?? 'unknown'}`);
        }
      }
      if (okCount > 0) {
        toast.success(`Processed ${okCount} of ${list.length} quote uploads.`);
      }
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [handleFile, processFile]);

  return {
    fileRef,
    busy,
    openFilePicker,
    handleFile,
    handleFiles,
  };
}
